/**
 * CR150: Public Layout Header
 * 
 * Renders the page header based on the published layout configuration.
 * Mirrors the admin preview header rendering but for the public site.
 * 
 * CR201: Fixed async logo resolution - fetches specific asset by ID if not in cache.
 * CR207: SECURITY FIX - Removed dangerouslySetInnerHTML, now uses SafeAssetImage with URL-based rendering.
 */

import { useMemo, useEffect, useState } from "react";
import { LayoutGrid } from "lucide-react";
import type { HeaderBlockConfig } from "@/lib/layout-studio/types";
import { DEFAULT_HEADER_CONFIG } from "@/lib/layout-studio/types";
import { resolveHeaderTypography } from "@/lib/layout-studio/headerTypographyPresets";
import { useAppAssets } from "@/hooks/useAppAssets";
import { supabase } from "@/integrations/supabase/client";
import { SafeAssetImage } from "@/components/ui/SafeAssetImage";

interface PublicLayoutHeaderProps {
  config: HeaderBlockConfig;
  loading?: boolean;
}

// NOTE: sanitizeSvg removed - we no longer use dangerouslySetInnerHTML on public pages.
// All SVG rendering is done via <img> tag (SafeAssetImage) which prevents XSS.

export function PublicLayoutHeader({ config, loading = false }: PublicLayoutHeaderProps) {
  // Merge with defaults
  const mergedConfig: HeaderBlockConfig = {
    ...DEFAULT_HEADER_CONFIG,
    ...config,
  };
  
  // CR285: Enhanced debug logging for header configuration tracing
  useEffect(() => {
    console.log("[PublicLayoutHeader] CR285 Config received:", {
      rawConfig: config,
      showLogo: mergedConfig.showLogo,
      showTitle: mergedConfig.showTitle,
      showSubtitle: mergedConfig.showSubtitle,
      titleText: mergedConfig.titleText,
      subtitleText: mergedConfig.subtitleText,
      logoAssetId: mergedConfig.logoAssetId,
      alignment: mergedConfig.alignment,
    });
  }, [config, mergedConfig]);

  // Load assets for logo resolution
  const { assets, loading: assetsLoading } = useAppAssets();
  
  // CR207: Track resolved logo asset (URL + SVG for SafeAssetImage)
  const [resolvedLogoAsset, setResolvedLogoAsset] = useState<{ url: string | null; svg: string | null } | null>(null);
  const [directFetchLoading, setDirectFetchLoading] = useState(false);

  // Resolve logo from Asset Studio cache first
  // NOTE: useAppAssets() with no filter returns ALL asset types (including 'icon')
  const logoAssetFromCache = useMemo(() => {
    if (!mergedConfig.logoAssetId) return null;
    const found = assets.find((a) => a.id === mergedConfig.logoAssetId);
    console.log("[PublicLayoutHeader] Logo asset lookup:", {
      logoAssetId: mergedConfig.logoAssetId,
      assetsCount: assets.length,
      found: !!found,
      foundName: found?.name,
      foundType: found?.type,
      hasUrl: !!found?.url,
    });
    return found || null;
  }, [mergedConfig.logoAssetId, assets]);

  // CR207: If logo not found in cache, fetch directly by ID (get URL + SVG for SafeAssetImage)
  useEffect(() => {
    const fetchLogoDirectly = async () => {
      if (!mergedConfig.logoAssetId) {
        console.log("[PublicLayoutHeader] No logoAssetId configured");
        setResolvedLogoAsset(null);
        return;
      }
      if (logoAssetFromCache) {
        console.log("[PublicLayoutHeader] Logo found in cache, using cached asset");
        setResolvedLogoAsset({ url: logoAssetFromCache.url || null, svg: logoAssetFromCache.svg });
        return;
      }
      if (assetsLoading) {
        console.log("[PublicLayoutHeader] Assets still loading, waiting...");
        return;
      }
      if (resolvedLogoAsset) {
        console.log("[PublicLayoutHeader] Already have resolved logo asset");
        return;
      }
      
      console.log("[PublicLayoutHeader] Fetching logo directly by ID:", mergedConfig.logoAssetId);
      setDirectFetchLoading(true);
      try {
        const { data, error } = await supabase
          .from("app_assets")
          .select("url, svg")
          .eq("id", mergedConfig.logoAssetId)
          .single();
        
        console.log("[PublicLayoutHeader] Direct fetch result:", { 
          hasData: !!data, 
          hasUrl: !!data?.url,
          hasSvg: !!data?.svg, 
          error: error?.message 
        });
        
        if (!error && data) {
          setResolvedLogoAsset({ url: data.url || null, svg: data.svg || null });
        } else if (error) {
          console.error("[PublicLayoutHeader] Direct fetch error:", error);
          setResolvedLogoAsset(null);
        }
      } catch (err) {
        console.error("[PublicLayoutHeader] Failed to fetch logo asset:", err);
        setResolvedLogoAsset(null);
      } finally {
        setDirectFetchLoading(false);
      }
    };
    
    fetchLogoDirectly();
  }, [mergedConfig.logoAssetId, logoAssetFromCache, assetsLoading, resolvedLogoAsset]);

  // CR207: Get logo props for SafeAssetImage (prefers URL, falls back to SVG data URI)
  const logoImageProps = useMemo(() => {
    if (logoAssetFromCache) {
      return { url: logoAssetFromCache.url || null, svg: logoAssetFromCache.svg };
    }
    return resolvedLogoAsset;
  }, [logoAssetFromCache, resolvedLogoAsset]);

  const hasLogo = logoImageProps && (logoImageProps.url || logoImageProps.svg);

  // CR285: Get title and subtitle text using nullish coalescing
  // Use ?? instead of || so empty strings are intentional (not falsy fallback)
  const titleText = mergedConfig.titleText ?? mergedConfig.titleOverride ?? "Deal Finder";
  // CR285: Use nullish coalescing for subtitle too
  const subtitleText = mergedConfig.subtitleText ?? mergedConfig.subtitleOverride ?? "Find the best gift card deals";

  // Resolve typography styles
  const { titleStyle, subtitleStyle } = resolveHeaderTypography(
    mergedConfig.headerTypography,
    null // themeTokens - not needed for public
  );

  // Logo size classes - also apply to SVG children
  const logoSizeClasses = {
    sm: "w-8 h-8 [&_svg]:w-full [&_svg]:h-full",
    md: "w-12 h-12 [&_svg]:w-full [&_svg]:h-full",
    lg: "w-16 h-16 [&_svg]:w-full [&_svg]:h-full",
  };
  const logoIconSize = {
    sm: "h-4 w-4",
    md: "h-6 w-6",
    lg: "h-8 w-8",
  };

  // If header block is completely hidden, don't render
  if (!mergedConfig.showLogo && !mergedConfig.showTitle) {
    return null;
  }

  // CR207: Show loading skeleton while layout or logo is loading
  const isLoading = loading || (mergedConfig.showLogo && mergedConfig.logoAssetId && !hasLogo && (assetsLoading || directFetchLoading));
  
  if (isLoading) {
    // Loading skeleton
    return (
      <header className="text-center py-8">
        <div className="inline-block mb-4">
          <div className="w-16 h-1 bg-muted mx-auto mb-4 animate-pulse" />
          <div className="h-8 w-48 bg-muted rounded animate-pulse mx-auto" />
          <div className="w-16 h-1 bg-muted mx-auto mt-4 animate-pulse" />
        </div>
        <div className="h-5 w-64 bg-muted rounded animate-pulse mx-auto mt-4" />
      </header>
    );
  }

  return (
    <header
      className={`py-6 ${
        mergedConfig.alignment === "center" ? "text-center" : "text-left"
      }`}
    >
      <div
        className="flex items-center gap-4"
        style={{
          justifyContent: mergedConfig.alignment === "center" ? "center" : "flex-start",
        }}
      >
        {/* Logo - CR207: Using SafeAssetImage instead of dangerouslySetInnerHTML for XSS protection */}
        {mergedConfig.showLogo && (
          <div className="inline-block">
            {mergedConfig.alignment === "center" && (
              <div className="w-16 h-1 bg-primary mx-auto mb-4" />
            )}
            <div
              className={`rounded flex items-center justify-center ${logoSizeClasses[mergedConfig.logoSize || "md"]}`}
            >
              {hasLogo ? (
                <SafeAssetImage
                  url={logoImageProps?.url}
                  svg={logoImageProps?.svg}
                  alt="Site logo"
                  className={logoSizeClasses[mergedConfig.logoSize || "md"]}
                  fallback={
                    <div className={`bg-primary/10 rounded flex items-center justify-center ${logoSizeClasses[mergedConfig.logoSize || "md"]}`}>
                      <LayoutGrid className={`${logoIconSize[mergedConfig.logoSize || "md"]} text-primary`} />
                    </div>
                  }
                />
              ) : (
                <div className={`bg-primary/10 rounded flex items-center justify-center ${logoSizeClasses[mergedConfig.logoSize || "md"]}`}>
                  <LayoutGrid className={`${logoIconSize[mergedConfig.logoSize || "md"]} text-primary`} />
                </div>
              )}
            </div>
            {mergedConfig.alignment === "center" && (
              <div className="w-16 h-1 bg-primary mx-auto mt-4" />
            )}
          </div>
        )}

        {/* Title and Subtitle */}
        {mergedConfig.showTitle && (
          <div>
            <h1
              className="text-3xl font-semibold tracking-tight text-primary"
              style={titleStyle}
            >
              {titleText}
            </h1>
            {mergedConfig.showSubtitle && subtitleText && (
              <p
                className="text-muted-foreground text-lg mt-2"
                style={subtitleStyle}
              >
                {subtitleText}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Banner text if configured */}
      {mergedConfig.bannerText && (
        <div className="mt-4 py-2 px-4 bg-primary/10 rounded text-sm text-primary font-medium inline-block">
          {mergedConfig.bannerText}
        </div>
      )}
    </header>
  );
}
